import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, X } from "lucide-react";
import {
  useProjectDependencies,
  useAddProjectDependency,
  useDeleteProjectDependency,
} from "@/hooks/useProjectDependencies";
import { usePortfolio } from "@/hooks/usePortfolio";
import { cn } from "@/lib/cn";
import type { Project } from "@/types/project";

/**
 * "Depends on" chips + inline editor (Brett, 2026-09-14 — Option B).
 *
 * Renders a project's upstream dependencies as small chips —
 * "after ETE-319" — amber while the blocker is unfinished, slate once
 * it completes. Visual information only; nothing is scheduled or
 * blocked by it. The same component serves the project hero and the
 * Top Projects board rows (compact popover editing there), so the two
 * surfaces can't drift.
 */

export function DependsOnChips({
  projectId,
  canEdit,
  compact = false,
}: {
  projectId: string;
  canEdit: boolean;
  /** Board mode: the "+ dep" affordance only appears on row hover. */
  compact?: boolean;
}) {
  // Cached portfolio — every consumer page already fetches this key,
  // so the chips never trigger an extra request.
  const portfolioQ = usePortfolio();
  const projects: Project[] = Array.isArray(portfolioQ.data) ? portfolioQ.data : [];
  const depsQ = useProjectDependencies();
  const addDep = useAddProjectDependency();
  const deleteDep = useDeleteProjectDependency();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState("");
  // The board's band cards are overflow-hidden, so an absolute popover
  // gets clipped at the card edge. Anchor a FIXED popover to the "+"
  // button instead — fixed positioning escapes the clip.
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  const deps = Array.isArray(depsQ.data) ? depsQ.data : [];
  const mine = deps.filter((d) => d.blocked_project_id === projectId);
  const byId = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const candidates = useMemo(() => {
    const taken = new Set(mine.map((d) => d.blocker_project_id));
    return projects
      .filter((p) => p.id !== projectId && !taken.has(p.id) && !p.archived_at)
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
  }, [projects, projectId, mine]);

  const add = () => {
    if (!picked) return;
    addDep.mutate(
      { blocker_project_id: picked, blocked_project_id: projectId },
      { onSuccess: () => { setPicked(""); setOpen(false); } },
    );
  };

  if (mine.length === 0 && !canEdit) return null;

  return (
    <span className={cn("relative inline-flex items-center gap-1", compact ? "min-w-0 flex-nowrap" : "flex-wrap")}>
      {/* Board rows keep one line (Brett, 2026-10-16: a wrapped chip made
          one row taller than the rest): the chips clip inside this
          span and "+ dep" stays visible after them. */}
      <span className={cn("inline-flex items-center gap-1", compact ? "min-w-0 flex-nowrap overflow-hidden" : "flex-wrap")}>
      {mine.map((d) => {
        const blocker = byId.get(d.blocker_project_id);
        const doneBlocker = blocker?.status === "complete";
        return (
          <span
            key={d.id}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold",
              doneBlocker
                ? "bg-raised text-ink-muted"
                : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
            )}
            title={
              blocker
                ? `Depends on ${blocker.name}${doneBlocker ? " (complete)" : ""}`
                : `Depends on ${d.blocker_project_id}`
            }
          >
            <Link2 className="h-2.5 w-2.5" />
            <Link
              to={`/portfolio/${d.blocker_project_id}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline"
            >
              after {d.blocker_project_id}
            </Link>
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteDep.mutate({
                    blocker_project_id: d.blocker_project_id,
                    blocked_project_id: projectId,
                  });
                }}
                className="text-current opacity-50 transition-opacity hover:opacity-100"
                title={`Remove dependency on ${d.blocker_project_id}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        );
      })}
      </span>

      {canEdit && (
        <button
          ref={anchorRef}
          onClick={(e) => {
            e.stopPropagation();
            if (!open) {
              const r = anchorRef.current?.getBoundingClientRect();
              setAnchor(r ? { top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 300) } : null);
            }
            setOpen(!open);
          }}
          // Always visible (Brett hunted for it, 2026-09-14) — quiet
          // gray, but present. Hidden-until-hover failed discoverability
          // the same way the click-to-cycle health dot did.
          className="shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-medium text-ink-subtle transition-colors hover:bg-raised hover:text-ink-3"
          title="Add a dependency — this project starts after another"
          aria-label={`Add dependency for ${projectId}`}
        >
          + dep
        </button>
      )}

      {open && (
        <>
          <button
            className="fixed inset-0 z-10 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-hidden
            tabIndex={-1}
          />
          <span
            className="fixed z-20 flex w-72 items-center gap-1.5 rounded-lg border border-line bg-surface p-2 shadow-lg"
            style={anchor ? { top: anchor.top, left: anchor.left } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <select
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-line bg-surface px-1.5 py-1 text-xs focus:border-brand-navy-400 focus:outline-none"
              aria-label={`Blocker project for ${projectId}`}
            >
              <option value="">Comes after…</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id} — {p.name.slice(0, 42)}
                </option>
              ))}
            </select>
            <button
              onClick={add}
              disabled={!picked || addDep.isPending}
              className="rounded-md bg-brand-navy-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-40"
            >
              Add
            </button>
          </span>
        </>
      )}
    </span>
  );
}
